import { useEffect, useRef } from "react";
import { X, Clock, ArrowRight } from "lucide-react";
import { menuItems } from "@/mocks/content";

type MenuItem = (typeof menuItems)[0];

interface MenuModalProps {
  item: MenuItem | null;
  onClose: () => void;
}

const menuDetails: Record<number, { highlights: string[]; note: string }> = {
  1: {
    highlights: [
      "毛穴クレンジング & ディープクレンジング",
      "高濃度美容液導入（イオン導入）",
      "リフトアップマッサージ",
      "保湿パック仕上げ",
    ],
    note: "敏感肌・乾燥肌の方にも対応可能です。カウンセリング時にお申し付けください。",
  },
  2: {
    highlights: [
      "全身アロマオイルトリートメント",
      "深層筋リリーステクニック",
      "リンパドレナージュ",
      "温熱ストーンオプション（+¥2,000）",
    ],
    note: "施術前後の水分補給をお勧めします。施術後はゆっくりお休みいただけます。",
  },
  3: {
    highlights: [
      "頭皮スキャン & カウンセリング",
      "クレンジングシャンプー",
      "頭皮マッサージ（指圧・ツボ押し）",
      "育毛美容液導入",
    ],
    note: "頭皮の状態に合わせた専用トリートメントをご用意しています。",
  },
  4: {
    highlights: [
      "骨格診断 & デザインカウンセリング",
      "シェービング & 形成",
      "眉毛ワックス脱毛",
      "仕上げ眉マスカーラー",
    ],
    note: "初回の方はカウンセリングに10分程度お時間をいただきます。",
  },
  5: {
    highlights: [
      "フェイシャルケア（60分）",
      "ボディリラクゼーション（60分）",
      "スカルプケア（30分）",
      "ウェルカムドリンク & アフタードリンク付き",
    ],
    note: "完全個室でのフルコース。特別なご褒美に最適なプレミアムプランです。",
  },
};

export default function MenuModal({ item, onClose }: MenuModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!item) return;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [item]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current) onClose();
  };

  const handleBooking = () => {
    onClose();
    setTimeout(() => {
      const ctaSection = document.getElementById("cta");
      if (ctaSection) ctaSection.scrollIntoView({ behavior: "smooth" });
    }, 300);
  };

  const details = item ? menuDetails[item.id] : null;

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{
        background: "rgba(0,0,0,0.85)",
        backdropFilter: "blur(6px)",
        opacity: item ? 1 : 0,
        pointerEvents: item ? "auto" : "none",
        transition: "opacity 0.35s cubic-bezier(0.23, 1, 0.32, 1)",
      }}
      onClick={handleOverlayClick}
      aria-modal="true"
      role="dialog"
      aria-label={item?.name ?? "メニュー詳細"}
    >
      <div
        className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        style={{
          background: "linear-gradient(160deg, #1C1C1C 0%, #141414 100%)",
          border: "1px solid rgba(212,163,115,0.15)",
          transform: item ? "translateY(0) scale(1)" : "translateY(24px) scale(0.97)",
          transition: "transform 0.4s cubic-bezier(0.23, 1, 0.32, 1)",
        }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center text-white/50 hover:text-white cursor-pointer"
          style={{ transition: "color 0.2s" }}
          aria-label="閉じる"
        >
          <X size={18} />
        </button>

        {/* Image */}
        {item && (
          <div className="relative overflow-hidden" style={{ paddingBottom: "50%" }}>
            <img
              src={item.image}
              alt={item.name}
              className="absolute inset-0 w-full h-full object-cover object-top"
              style={{ filter: "brightness(0.75)" }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#141414] via-black/30 to-transparent" />
            {/* Duration badge */}
            <div className="absolute top-4 left-4 flex items-center gap-1.5 bg-black/60 backdrop-blur-sm px-3 py-1.5">
              <Clock size={11} className="text-[#D4A373]" />
              <span
                className="text-white/80 text-xs"
                style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
              >
                {item.duration}
              </span>
            </div>
          </div>
        )}

        {/* Content */}
        {item && details && (
          <div className="px-6 md:px-8 pb-8 pt-4">
            {/* Title & Price */}
            <div className="flex items-start justify-between gap-4 mb-5">
              <div>
                <p
                  className="text-[#D4A373] text-xs tracking-[0.3em] uppercase mb-1"
                  style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                >
                  Treatment
                </p>
                <h2
                  className="text-white text-xl md:text-2xl font-medium"
                  style={{ fontFamily: "'Noto Serif JP', serif", letterSpacing: "0.06em" }}
                >
                  {item.name}
                </h2>
              </div>
              <div className="text-right shrink-0">
                <p
                  className="text-[#D4A373] text-xl md:text-2xl font-medium"
                  style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                >
                  {item.price}
                </p>
                <p
                  className="text-white/40 text-xs mt-0.5"
                  style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                >
                  税込
                </p>
              </div>
            </div>

            {/* Divider */}
            <div className="w-full h-px bg-white/8 mb-5" />

            {/* Description */}
            <p
              className="text-white/60 text-sm leading-relaxed mb-6"
              style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
            >
              {item.description}
            </p>

            {/* Highlights */}
            <div className="mb-6">
              <p
                className="text-[#D4A373] text-xs tracking-[0.25em] uppercase mb-3"
                style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
              >
                Contents
              </p>
              <ul className="space-y-2">
                {details.highlights.map((h, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 text-white/70 text-sm"
                    style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                  >
                    <span className="mt-1.5 w-1 h-1 rounded-full bg-[#D4A373] shrink-0" />
                    {h}
                  </li>
                ))}
              </ul>
            </div>

            {/* Note */}
            <div
              className="px-4 py-3 mb-7"
              style={{ background: "rgba(212,163,115,0.06)", borderLeft: "2px solid rgba(212,163,115,0.4)" }}
            >
              <p
                className="text-white/40 text-xs leading-relaxed"
                style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
              >
                {details.note}
              </p>
            </div>

            {/* CTA */}
            <button
              onClick={handleBooking}
              className="w-full flex items-center justify-center gap-2 py-3.5 cursor-pointer group"
              style={{
                background: "linear-gradient(135deg, #D4A373 0%, #C8956A 100%)",
                fontFamily: "'Noto Sans JP', sans-serif",
                letterSpacing: "0.15em",
              }}
            >
              <span className="text-white text-sm font-medium whitespace-nowrap">このメニューを予約する</span>
              <ArrowRight
                size={14}
                className="text-white"
                style={{ transition: "transform 0.25s", transform: "translateX(0)" }}
              />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
